import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Tv, Smartphone, Gamepad2, Coffee, ShoppingCart, Utensils, Music, GraduationCap } from 'lucide-react';
import { getAudioCtx, playMateChime } from '@/lib/mateSound';

// Lightweight intro animation shown before the login screen.
// A circular roulette of "distractions" spins, decelerates and a pointer
// lands on the MATE logo: "it's MATE time". CSS-only (transform/opacity),
// vector icons (no extra deps, no remote images) => no performance impact.

const DISTRACTIONS = [
  { Icon: Tv, color: '#6366f1' },
  { Icon: Smartphone, color: '#0ea5e9' },
  { Icon: Gamepad2, color: '#ec4899' },
  { Icon: Coffee, color: '#a16207' },
  { Icon: ShoppingCart, color: '#16a34a' },
  { Icon: Utensils, color: '#f59e0b' },
  { Icon: Music, color: '#8b5cf6' },
];

const RADIUS = 110; // px from center to each item
const SPIN_MS = 4200; // duration of the spinning deceleration (slow)
const SELECT_MS = 1400; // pulse/highlight + center legend on MATE after landing
const FADE_MS = 450; // overlay fade-out

const IntroSpinner = ({ logoUrl, onDone }) => {
  // items: index 0 is MATE (sits at the top, under the pointer); rest are distractions
  const items = [{ mate: true }, ...DISTRACTIONS.map((d) => ({ ...d }))];
  const n = items.length;

  const [rotation, setRotation] = useState(0);
  const [phase, setPhase] = useState('spinning'); // spinning -> selected -> fading
  const timers = useRef([]);
  const finished = useRef(false);
  const playedRef = useRef(false);
  const fallbackHandlerRef = useRef(null);

  const clearTimers = () => {
    timers.current.forEach((t) => clearTimeout(t));
    timers.current = [];
  };

  // Play the chime once (guarded).
  const doPlayChime = useCallback(() => {
    if (playedRef.current) return;
    playedRef.current = true;
    playMateChime();
  }, []);

  // Attempt to play now; works if the AudioContext is already unlocked
  // (e.g. user already interacted / media engagement). If blocked, the
  // first-gesture listener armed on mount will play it on the first tap.
  const triggerChime = useCallback(() => {
    const ctx = getAudioCtx();
    if (!ctx) return;
    if (ctx.state === 'running') {
      doPlayChime();
    } else {
      ctx.resume().then(() => doPlayChime()).catch(() => {});
    }
  }, [doPlayChime]);

  const finish = useCallback(() => {
    if (finished.current) return;
    finished.current = true;
    clearTimers();
    setPhase('fading');
    const t = setTimeout(() => onDone && onDone(), FADE_MS);
    timers.current.push(t);
  }, [onDone]);

  useEffect(() => {
    // Arm audio unlock on the FIRST user gesture anywhere (tap/click/key), from
    // the moment the intro mounts. This is the reliable path on mobile and on
    // desktop: the browser blocks audio until a gesture, so we unlock + play on
    // the very first interaction (even if the user taps early to skip).
    const ctx = getAudioCtx(); // pre-create (suspended) so resume() is instant
    const unlockAndPlay = () => {
      const c = getAudioCtx();
      if (!c) return;
      if (c.state === 'running') {
        doPlayChime();
      } else {
        c.resume().then(() => doPlayChime()).catch(() => {});
      }
    };
    fallbackHandlerRef.current = unlockAndPlay;
    const gestureEvents = ['pointerdown', 'touchstart', 'keydown'];
    gestureEvents.forEach((ev) => window.addEventListener(ev, unlockAndPlay, { once: true }));

    // Force the initial paint with a double requestAnimationFrame so the CSS
    // transition runs on slower phones/tablets instead of jumping to the end.
    let raf1 = 0;
    let raf2 = 0;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        // Spin several full turns and land MATE back at the top (multiple of 360).
        setRotation(360 * 5);
      });
    });

    const t1 = setTimeout(() => {
      setPhase('selected');
      triggerChime(); // plays at the climax if audio already unlocked
    }, SPIN_MS + 100);
    const t2 = setTimeout(() => finish(), SPIN_MS + SELECT_MS + 100);
    timers.current.push(t1, t2);

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      clearTimers();
      if (fallbackHandlerRef.current) {
        gestureEvents.forEach((ev) => window.removeEventListener(ev, fallbackHandlerRef.current));
        fallbackHandlerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center cursor-pointer"
      style={{
        background: 'linear-gradient(to bottom right, #fff7ed, #f1f5f9)',
        opacity: phase === 'fading' ? 0 : 1,
        transition: `opacity ${FADE_MS}ms ease`,
      }}
      onClick={finish}
      data-testid="intro-spinner"
      role="button"
      aria-label="Saltar animación"
    >
      <div className="flex flex-col items-center select-none">
        {/* Pointer */}
        <div
          className="mb-1"
          style={{
            width: 0,
            height: 0,
            borderLeft: '12px solid transparent',
            borderRight: '12px solid transparent',
            borderTop: '18px solid #f97316',
            filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.15))',
            transform: phase === 'selected' ? 'translateY(2px) scale(1.1)' : 'none',
            transition: 'transform 200ms ease',
          }}
          data-testid="intro-pointer"
        />

        {/* Wheel */}
        <div
          className="relative rounded-full"
          style={{
            width: RADIUS * 2 + 96,
            height: RADIUS * 2 + 96,
            background: 'radial-gradient(circle at center, #ffffff 0%, #f8fafc 70%, #eef2f7 100%)',
            boxShadow: '0 10px 30px rgba(0,0,0,0.12), inset 0 0 0 2px rgba(148,163,184,0.25)',
          }}
        >
          <div
            className="absolute inset-0"
            style={{
              transform: `rotate(${rotation}deg)`,
              transition: `transform ${SPIN_MS}ms cubic-bezier(0.16, 1, 0.3, 1)`,
              willChange: 'transform',
            }}
          >
            {items.map((item, i) => {
              const angle = (360 / n) * i;
              const isMate = item.mate;
              const highlight = isMate && phase === 'selected';
              return (
                <div
                  key={i}
                  className="absolute top-1/2 left-1/2 flex items-center justify-center rounded-2xl"
                  style={{
                    width: 60,
                    height: 60,
                    marginTop: -30,
                    marginLeft: -30,
                    transform: `rotate(${angle}deg) translateY(-${RADIUS}px) rotate(${-angle}deg) ${highlight ? 'scale(1.25)' : 'scale(1)'}`,
                    transition: 'transform 250ms ease',
                    background: isMate ? '#fff' : '#ffffffcc',
                    boxShadow: highlight
                      ? '0 0 0 3px #f97316, 0 6px 16px rgba(249,115,22,0.45)'
                      : '0 2px 8px rgba(0,0,0,0.10)',
                  }}
                  data-testid={isMate ? 'intro-item-mate' : `intro-item-${i}`}
                >
                  {isMate ? (
                    logoUrl ? (
                      <img src={logoUrl} alt="MATE" className="w-full h-full object-contain rounded-2xl p-1" />
                    ) : (
                      <GraduationCap className="w-8 h-8" style={{ color: '#f97316' }} />
                    )
                  ) : (
                    <item.Icon className="w-7 h-7" style={{ color: item.color }} />
                  )}
                </div>
              );
            })}
          </div>

          {/* Center legend shown when the pointer lands on MATE */}
          <div
            className="absolute inset-0 flex items-center justify-center pointer-events-none px-6 text-center"
            style={{
              opacity: phase === 'selected' ? 1 : 0,
              transform: phase === 'selected' ? 'scale(1)' : 'scale(0.85)',
              transition: 'opacity 300ms ease, transform 300ms ease',
            }}
            data-testid="intro-mate-legend"
          >
            <span
              className="text-xl font-extrabold leading-tight flex flex-col items-center"
              style={{ color: '#f97316', fontFamily: 'Plus Jakarta Sans, sans-serif', textShadow: '0 1px 2px rgba(0,0,0,0.08)' }}
            >
              <span>¡Es tiempo de</span>
              <span className="text-2xl">MATE!</span>
            </span>
          </div>
        </div>

        <p className="mt-6 text-xs text-slate-400" data-testid="intro-skip-hint">
          Toca la pantalla para saltar
        </p>
      </div>
    </div>
  );
};

export default IntroSpinner;
